import { Box, Typography } from "@outschool/backpack";
import { isLiveOneTime } from "@outschool/business-rules";
import { Activity as ActivityType } from "@outschool/gql-backend-generated";
import { ActivityListing_ListingFragment } from "@outschool/gql-frontend-generated";
import { TFunction } from "@outschool/localization";
import { plural } from "@outschool/text";
import { TrackingProperties } from "@outschool/ui-legacy-component-library";
import { Screen } from "@outschool/ui-utils";
import capitalize from "lodash/capitalize";
import React from "react";

import * as Activity from "../../../shared/Activity";
import * as Section from "../../../shared/Section";
import { useIsActivitySavedByCurrentUser } from "../saving/graphql/hooks";
import { useMembershipRequirement } from "../subscriptions/hooks/useMembershipRequirement";
import { DesktopListing } from "./DesktopListing";
import { MobileListing } from "./MobileListing";

export function Listings({
  children,
  title,
}: React.PropsWithChildren<{ title?: string; className?: string }>) {
  if (!React.Children.map(children, c => c)?.length) {
    return null;
  }

  return (
    <Box>
      {!!title && (
        <Box
          flex
          sx={{
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h3">{title}</Typography>
        </Box>
      )}
      <Box>{children}</Box>
    </Box>
  );
}

type ScheduleTextActivity = Pick<
  ActivityType,
  | "duration_weeks"
  | "weekly_meetings"
  | "is_ongoing_weekly"
  | "isClub"
  | "isSelfPaced"
> & { hasTeacherSchedule?: boolean };

export function scheduleText(
  activity: ScheduleTextActivity,
  t?: TFunction,
): [string, string?] {
  if (!t) {
    return scheduleTextNoT(activity);
  }
  const { duration_weeks: durationWeeks, weekly_meetings: weeklyMeetings } =
    activity;
  let frequencyText: string | undefined = undefined;
  let durationText: string | undefined = undefined;
  if (!!activity.is_ongoing_weekly) {
    switch (weeklyMeetings) {
      case 1:
        frequencyText = t`Meets once per week`;
        break;
      case 2:
        frequencyText = t`Meets twice per week`;
        break;
      default:
        // @ts-ignore TS(2769): No overload matches this call.
        frequencyText = t("Meets {{numWeeklyMeetings}} times per week", {
          numWeeklyMeetings: weeklyMeetings,
          count: weeklyMeetings || 0,
        });
    }
  } else if (activity.isSelfPaced === true) {
    frequencyText = t("Self-paced");
  } else if (Activity.isFlexSchedule(activity) === true) {
    // @ts-ignore TS(2769): No overload matches this call.
    frequencyText = t("Flexible for {{numDurationWeeks}} week", {
      numDurationWeeks: durationWeeks,
      count: durationWeeks || 0,
      defaultValue_plural: "Flexible for {{numDurationWeeks}} weeks",
    });
  } else if (isLiveOneTime(activity) === true) {
    frequencyText = t`Meets once`;
  } else if (durationWeeks === 1) {
    switch (weeklyMeetings) {
      case 1:
        frequencyText = t`Meets once`;
        break;
      case 2:
        frequencyText = t`Meets twice `;
        break;
      default:
        // @ts-ignore TS(2769): No overload matches this call.
        frequencyText = t("Meets {{numWeeklyMeetings}} times", {
          numWeeklyMeetings: weeklyMeetings,
          count: weeklyMeetings || 0,
        });
    }
    durationText = t`Over 1 week`;
  } else if (activity.hasTeacherSchedule) {
    return [t`Meets on demand`, undefined];
  } else {
    switch (weeklyMeetings) {
      case 1:
        frequencyText = t`Meets once per week`;
        break;
      case 2:
        frequencyText = t`Meets twice per week`;
        break;
      default:
        // @ts-ignore TS(2769): No overload matches this call.
        frequencyText = t("Meets {{numWeeklyMeetings}} times per week", {
          numWeeklyMeetings: weeklyMeetings,
          count: weeklyMeetings || 0,
        });
    }
    // @ts-ignore TS(2769): No overload matches this call.
    durationText = t("Over {{numDurationWeeks}} week", {
      numDurationWeeks: durationWeeks,
      count: durationWeeks || 0,
      defaultValue_plural: "Over {{numDurationWeeks}} weeks",
    });
  }
  // @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
  return [frequencyText, durationText];
}

function scheduleTextNoT(activity: ScheduleTextActivity): [string, string?] {
  const frequencyLanguage = (frequency: number): string => {
    if (frequency === 1) {
      return "once";
    } else if (frequency === 2) {
      return "twice";
    } else {
      return `${frequency} times`;
    }
  };
  const { duration_weeks: durationWeeks, weekly_meetings: weeklyMeetings } =
    activity;
  let frequencyText: string | undefined = undefined;
  let durationText: string | undefined = undefined;
  if (!!activity.is_ongoing_weekly) {
    // @ts-ignore TS(2345): Argument of type 'Maybe<number>' is not assignable... Remove this comment to see the full error message
    frequencyText = `meets ${frequencyLanguage(weeklyMeetings)} per week`;
  } else if (activity.isSelfPaced === true) {
    frequencyText = "self-paced";
  } else if (Activity.isFlexSchedule(activity) === true) {
    frequencyText = `flexible for ${durationWeeks} ${plural(
      "week",
      // @ts-ignore TS(2345): Argument of type 'Maybe<number>' is not assignable... Remove this comment to see the full error message
      durationWeeks,
    )}`;
  } else if (isLiveOneTime(activity) === true) {
    frequencyText = "meets once";
  } else if (durationWeeks === 1) {
    // @ts-ignore TS(2345): Argument of type 'Maybe<number>' is not assignable... Remove this comment to see the full error message
    frequencyText = `meets ${frequencyLanguage(weeklyMeetings)}`;
    durationText = `over 1 week`;
  } else if (activity.hasTeacherSchedule) {
    frequencyText = `meets on demand`;
  } else {
    // @ts-ignore TS(2345): Argument of type 'Maybe<number>' is not assignable... Remove this comment to see the full error message
    frequencyText = `${frequencyLanguage(weeklyMeetings)} per week`;
    // @ts-ignore TS(2345): Argument of type 'Maybe<number>' is not assignable... Remove this comment to see the full error message
    durationText = `over ${durationWeeks} ${plural("week", durationWeeks)}`;
  }
  return [capitalize(frequencyText), durationText && capitalize(durationText)];
}

/**
 * Props for tracking client-side activity events
 *
 * Optional fields are best-effort with available data
 *
 * TODO: Investigate whether any analytics reports actually use these optional fields
 */
export interface ActivityTrackingProps {
  activity_uid: string | null;
  /** @optional */
  activity_age_max?: number | null;
  /** @optional */
  activity_age_min?: number | null;
  /** @optional */
  activity_average_star_rating?: number | null;
  /** @optional */
  activity_duration_minutes?: number | null;
  /** @optional */
  activity_duration_weeks?: number | null;
  /** @optional */
  activity_is_club?: string | null;
  /** @optional */
  activity_is_ongoing_weekly?: string | null;
  /** @optional */
  activity_price_cents?: number | null;
  /** @optional */
  activity_review_count?: number | null;
  /** @optional */
  activity_weekly_meetings?: number | null;
  /** @optional id of the next available section */
  section_uid?: string | null;
  /** @optional */
  first_section_filled_space_count?: number | null;
  /** @optional */
  first_section_size_max?: number | null;
  /** @optional */
  first_section_start_time?: string;
  /** @optional */
  leader_average_activity_star_rating?: number | null;
  /** @optional */
  leader_review_count?: number | null;
}

function activityTrackingProps(
  listing: ActivityListing_ListingFragment,
): ActivityTrackingProps {
  return {
    activity_uid: listing.activity?.uid,
    activity_age_max: listing.activity?.age_max,
    activity_age_min: listing.activity?.age_min,
    activity_average_star_rating: listing.activity?.averageStarRating,
    activity_duration_minutes: listing.activity?.duration_minutes,
    activity_duration_weeks: listing.activity?.duration_weeks,
    activity_is_club: listing.activity?.isClub?.toString(),
    activity_is_ongoing_weekly: listing.activity?.is_ongoing_weekly?.toString(),
    activity_price_cents: listing.activity?.price_cents,
    activity_review_count: listing.activity?.reviewCount,
    activity_weekly_meetings: listing.activity?.weekly_meetings,
    section_uid: listing.nextSection?.uid,
    first_section_filled_space_count: listing.nextSection?.filledSpaceCount,
    first_section_size_max: listing.nextSection?.size_max,
    first_section_start_time: listing.nextSection?.start_time,
    leader_average_activity_star_rating:
      listing.leader?.averageActivityStarRating,
    leader_review_count: listing.leader?.reviewCount,
  };
}

export type ActivityListingProps = {
  listing: ActivityListing_ListingFragment;
  sectionSearchFilters?: Section.SectionSearchFilters;
  trackingProperties?: TrackingProperties;
  disabled?: boolean;
  openInNewTab?: boolean;
  listingIndex?: number;
  /**
   * Replace the details with a custom component (e.g. review actions)
   *
   * TODO: Should this component receive reviewItem directly and handle the rendering?
   */
  listingDetailsOverride?: React.ReactNode;
  showFundingBadge?: boolean;
  isSelected?: boolean;
};

/**
 * Displays a single activity's row on the listing page.
 * Props expected: history, brief, activity.
 */
export const ActivityListing = ({
  listing,
  listingIndex,
  sectionSearchFilters,
  trackingProperties = {},
  disabled = false,
  openInNewTab = false,
  listingDetailsOverride,
  showFundingBadge,
  isSelected,
}: ActivityListingProps) => {
  const isActivitySavedByCurrentUser = useIsActivitySavedByCurrentUser(
    listing.activity,
  );
  const { nextSection, activity, leader } = listing;
  const { showCredits } = useMembershipRequirement();

  const isMobile = Screen.useIsMobile();
  const insertionId = listing.insertionId;

  const additionalTrackingProperties = activityTrackingProps(listing);

  if (isMobile) {
    return (
      <MobileListing
        activity={activity}
        nextSection={nextSection}
        leader={leader}
        sectionSearchFilters={sectionSearchFilters}
        trackingProperties={{
          ...trackingProperties,
          ...additionalTrackingProperties,
        }}
        disabled={disabled}
        insertionId={insertionId}
        listingIndex={listingIndex}
        showCredits={showCredits}
        showFundingBadge={showFundingBadge}
        listingDetailsOverride={listingDetailsOverride}
      />
    );
  } else {
    return (
      <DesktopListing
        activity={activity}
        nextSection={nextSection}
        leader={leader}
        isActivitySavedByCurrentUser={isActivitySavedByCurrentUser}
        trackingProperties={{
          ...trackingProperties,
          ...additionalTrackingProperties,
        }}
        disabled={disabled}
        openInNewTab={openInNewTab}
        insertionId={insertionId}
        listingIndex={listingIndex}
        listingDetailsOverride={listingDetailsOverride}
        showCredits={showCredits}
        showFundingBadge={showFundingBadge}
        isSelected={isSelected}
      />
    );
  }
};
